import { z } from 'zod';
import { CategoryAccent, CategoryKey } from './taxonomy.js';
import { StorageObjectKey } from './storage.js';
import { E164Phone } from './auth.js';
import { MemberLocation } from './profile.js';
const Id = z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .max(48);
const Copy = z.string().trim().min(1).max(160);
export const LocalizedCopy = z.object({ en: Copy }).catchall(Copy);
export const OnboardingOption = z
    .object({
    id: Id,
    label: LocalizedCopy,
    iconKey: Id.optional(),
    accentToken: CategoryAccent.optional(),
    order: z.number().int().min(0),
    enabled: z.boolean(),
})
    .strict();
export const OnboardingOptionGroup = z
    .object({
    id: Id,
    options: z.array(OnboardingOption).min(1).max(40),
})
    .strict();
/** Renderer IDs are code-owned; copy, order and option IDs belong to the revision. */
export const OnboardingSection = z
    .object({
    id: Id,
    title: LocalizedCopy,
    renderer: z.enum([
        'category_select',
        'role_tags',
        'intended_actions',
        'community_location',
        'category_priority',
        'privacy',
    ]),
    order: z.number().int().min(0),
    optionGroupId: Id.optional(),
    required: z.boolean(),
    fields: z
        .array(z
        .object({
        id: z.enum(['country', 'city', 'state', 'postal_code', 'radius']),
        label: LocalizedCopy,
        required: z.boolean(),
        order: z.number().int().min(0),
        optionGroupId: Id.optional(),
    })
        .strict())
        .max(5)
        .optional(),
    optionGroupIds: z.array(Id).max(3).optional(),
})
    .strict();
export const OnboardingQuestion = z
    .object({
    id: Id,
    prompt: LocalizedCopy,
    type: z.enum(['single_choice', 'multi_choice']),
    optionGroupId: Id,
    required: z.boolean(),
    minSelections: z.number().int().min(0).max(20),
    maxSelections: z.number().int().min(1).max(20),
    /** Visual choice treatment; data remains identical for either presentation. */
    presentation: z.enum(['chips', 'tiles']).optional(),
    order: z.number().int().min(0),
})
    .strict();
export const OnboardingDetailCard = z
    .object({
    id: Id,
    title: LocalizedCopy,
    iconKey: Id.optional(),
    accentToken: CategoryAccent.optional(),
    order: z.number().int().min(0),
    showWhenAnyCategoryKeys: z.array(CategoryKey).min(1).max(12),
    skippable: z.boolean(),
    questions: z.array(OnboardingQuestion).min(1).max(12),
})
    .strict();
export const OnboardingStep = z
    .object({
    id: z.enum(['identity', 'experience', 'details', 'photos']),
    title: LocalizedCopy,
    order: z.number().int().min(0),
    sections: z.array(OnboardingSection).max(12),
    fields: z
        .array(z
        .object({
        id: z.enum([
            'display_name',
            'date_of_birth',
            'community_role',
            'gender',
            'phone',
            'location',
            'citizenship',
        ]),
        label: LocalizedCopy,
        order: z.number().int().min(0),
        required: z.boolean(),
        optionGroupId: Id.optional(),
    })
        .strict())
        .max(8)
        .optional(),
})
    .strict();
export const OnboardingWelcomeAction = z
    .object({
    id: Id,
    label: LocalizedCopy,
    destination: z.enum(['my_onov', 'browse_groups', 'browse_feed']),
    order: z.number().int().min(0),
})
    .strict();
export const OnboardingFlowRevision = z
    .object({
    revision: z.number().int().positive(),
    rendererSchemaVersion: z.literal(1),
    steps: z.array(OnboardingStep).length(4),
    detailCards: z.array(OnboardingDetailCard).max(20),
    welcomeActions: z.array(OnboardingWelcomeAction).min(1).max(8),
    optionGroups: z.array(OnboardingOptionGroup).max(30),
})
    .strict();
export const OnboardingFlowBody = OnboardingFlowRevision.omit({ revision: true });
export function onboardingFlowBody(flow) {
    const { revision: _revision, ...body } = flow;
    return body;
}
const ordered = (items) => [...items].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
/** Wire order follows the published order values, including nested options/questions. */
export function orderedOnboardingFlow(flow) {
    return {
        ...flow,
        steps: ordered(flow.steps).map((step) => ({
            ...step,
            sections: ordered(step.sections).map((section) => ({
                ...section,
                ...(section.fields ? { fields: ordered(section.fields) } : {}),
            })),
            ...(step.fields ? { fields: ordered(step.fields) } : {}),
        })),
        detailCards: ordered(flow.detailCards).map((card) => ({
            ...card,
            questions: ordered(card.questions),
        })),
        welcomeActions: ordered(flow.welcomeActions),
        optionGroups: flow.optionGroups.map((group) => ({
            ...group,
            options: ordered(group.options),
        })),
    };
}
export const OnboardingFlowQuery = z
    .object({
    revision: z.coerce.number().int().positive().optional(),
})
    .strict();
export const OnboardingOptionCatalogue = z.object({
    revision: z.number().int().positive(),
    groups: z.array(OnboardingOptionGroup),
});
export const OnboardingStepId = z.enum(['identity', 'experience', 'details', 'photos']);
const DraftString = (max) => z.string().max(max).nullable().optional();
const DraftIds = z.array(Id).max(40).nullable().optional();
const DraftCategories = z.array(CategoryKey).max(40).nullable().optional();
/** Draft strings may be incomplete; Continue validates canonical values. */
export const IdentityStepDraft = z
    .object({
    displayName: DraftString(80),
    dateOfBirth: DraftString(10),
    communityRoleId: DraftString(48),
    gender: DraftString(30),
    location: DraftString(160),
    locationDetails: MemberLocation.nullable().optional(),
    citizenship: DraftString(60),
    phoneInput: DraftString(25),
})
    .strict();
export const ExperienceStepDraft = z
    .object({
    categoryKeys: DraftCategories,
    roleTagIds: DraftIds,
    intendedActionIds: DraftIds,
    communityScopeId: DraftString(48),
    country: DraftString(80),
    city: DraftString(80),
    state: DraftString(80),
    postalCode: DraftString(20),
    radiusId: DraftString(48),
    priorityCategoryKeys: DraftCategories,
    profileVisibilityId: DraftString(48),
    locationVisibilityId: DraftString(48),
    groupInvitationsId: DraftString(48),
})
    .strict();
const DetailCardAnswerDraft = z
    .object({
    state: z.enum(['unanswered', 'answered', 'skipped']),
    answers: z.record(Id, z.array(Id).max(20)).optional(),
})
    .strict();
export const DetailsStepDraft = z
    .object({
    cards: z.record(Id, DetailCardAnswerDraft).nullable().optional(),
})
    .strict();
export const OnboardingPhotoReference = z
    .object({
    key: StorageObjectKey,
})
    .strict();
/** Code-owned behaviors; catalogue labels and availability can change independently. */
export const OnboardingPhotoMode = z.enum(['same', 'different']);
export const PhotosStepDraft = z
    .object({
    mode: OnboardingPhotoMode.nullable().optional(),
    mainPhoto: OnboardingPhotoReference.nullable().optional(),
    categoryPhotos: z
        .record(CategoryKey, OnboardingPhotoReference.nullable())
        .nullable()
        .optional(),
})
    .strict();
export const OnboardingStepData = z
    .object({
    identity: IdentityStepDraft.optional(),
    experience: ExperienceStepDraft.optional(),
    details: DetailsStepDraft.optional(),
    photos: PhotosStepDraft.optional(),
})
    .strict();
const SaveBase = {
    version: z.number().int().min(0),
    flowRevision: z.number().int().positive(),
    intent: z.enum(['save_draft', 'continue']),
};
export const SaveOnboardingStepInput = z.discriminatedUnion('stepId', [
    z.object({ ...SaveBase, stepId: z.literal('identity'), data: IdentityStepDraft }).strict(),
    z.object({ ...SaveBase, stepId: z.literal('experience'), data: ExperienceStepDraft }).strict(),
    z.object({ ...SaveBase, stepId: z.literal('details'), data: DetailsStepDraft }).strict(),
    z.object({ ...SaveBase, stepId: z.literal('photos'), data: PhotosStepDraft }).strict(),
]);
export const OnboardingDraftView = z
    .object({
    flowRevision: z.number().int().positive(),
    version: z.number().int().min(0),
    stepData: OnboardingStepData,
    stepStatus: z.partialRecord(OnboardingStepId, z.enum(['incomplete', 'complete'])),
    lastVisitedStepId: OnboardingStepId,
    resumeStepId: OnboardingStepId,
    /** The account-owned binding used to validate an optional Identity phone. */
    verifiedPhone: E164Phone.nullable().optional(),
})
    .strict();
export const OnboardingStatusView = z.discriminatedUnion('status', [
    z.object({ status: z.literal('not_required') }),
    z.object({ status: z.literal('in_progress'), draft: OnboardingDraftView }),
    z.object({
        status: z.literal('completed'),
        completedAt: z.string().datetime(),
        receiptId: z.string(),
    }),
]);
export const StartOnboardingPhoneBindingInput = z.object({ phone: E164Phone }).strict();
export const ConfirmOnboardingPhoneBindingInput = z
    .object({
    phone: E164Phone,
    code: z.string().regex(/^\d{4,6}$/, '4–6 digit code'),
})
    .strict();
export const OnboardingPhoneBound = z
    .object({ phone: E164Phone, verified: z.literal(true) })
    .strict();
export { OtpStarted as OnboardingPhoneBindingStarted } from './auth.js';
export const CompleteOnboardingInput = z
    .object({
    version: z.number().int().min(0),
    flowRevision: z.number().int().positive(),
    idempotencyKey: z.string().uuid(),
})
    .strict();
export const PublishOnboardingFlowInput = z
    .object({
    baseRevision: z.number().int().positive(),
    flow: OnboardingFlowBody,
    reason: z.string().trim().min(3).max(300),
})
    .strict();
/** Checks relationships that individual wire schemas cannot express. */
export function validateOnboardingFlow(flow) {
    const errors = [];
    const unique = (ids, path) => {
        if (new Set(ids).size !== ids.length)
            errors.push(`${path}: duplicate ID`);
    };
    const sorted = (items, path) => {
        unique(items.map((item) => String(item.order)), `${path}.order`);
    };
    unique(flow.steps.map((step) => step.id), 'steps');
    sorted(flow.steps, 'steps');
    if (flow.steps.map((step) => step.id).join(',') !== 'identity,experience,details,photos') {
        errors.push('steps: unsupported order');
    }
    const identity = flow.steps.find((step) => step.id === 'identity');
    if (!identity?.fields?.some((field) => field.id === 'display_name' && field.required))
        errors.push('identity: name is required');
    if (!identity?.fields?.some((field) => field.id === 'date_of_birth' && field.required))
        errors.push('identity: date of birth is required');
    if (!identity?.fields?.some((field) => field.id === 'community_role' &&
        field.required &&
        field.optionGroupId === 'community_roles'))
        errors.push('identity: community role is required');
    if (identity?.fields) {
        unique(identity.fields.map((field) => field.id), 'identity.fields');
        sorted(identity.fields, 'identity.fields');
    }
    const experience = flow.steps.find((step) => step.id === 'experience');
    const requiredRenderers = [
        'category_select',
        'role_tags',
        'intended_actions',
        'community_location',
        'category_priority',
        'privacy',
    ];
    for (const renderer of requiredRenderers) {
        if (experience?.sections.filter((section) => section.renderer === renderer).length !== 1)
            errors.push(`experience: exactly one ${renderer} section required`);
    }
    for (const renderer of ['category_select', 'category_priority', 'privacy']) {
        if (!experience?.sections.find((section) => section.renderer === renderer)?.required)
            errors.push(`experience: ${renderer} must be required`);
    }
    unique(flow.optionGroups.map((group) => group.id), 'optionGroups');
    const groups = new Map(flow.optionGroups.map((group) => [group.id, group]));
    for (const group of flow.optionGroups) {
        unique(group.options.map((option) => option.id), `optionGroups.${group.id}`);
        sorted(group.options, `optionGroups.${group.id}`);
    }
    const photoModes = groups.get('photo_mode');
    if (!photoModes)
        errors.push('photos: photo_mode option group is required');
    else {
        for (const option of photoModes.options) {
            if (!OnboardingPhotoMode.safeParse(option.id).success)
                errors.push(`photos.photo_mode: unsupported mode ${option.id}`);
        }
        if (!photoModes.options.some((option) => option.enabled && OnboardingPhotoMode.safeParse(option.id).success))
            errors.push('photos.photo_mode: at least one supported enabled mode is required');
    }
    for (const step of flow.steps) {
        unique(step.sections.map((section) => section.id), `steps.${step.id}.sections`);
        sorted(step.sections, `steps.${step.id}.sections`);
        for (const section of step.sections) {
            if (section.optionGroupId && !groups.has(section.optionGroupId))
                errors.push(`steps.${step.id}.${section.id}: unknown option group`);
            if (['role_tags', 'intended_actions', 'community_location'].includes(section.renderer) &&
                !section.optionGroupId)
                errors.push(`steps.${step.id}.${section.id}: option group required`);
            const requiredGroup = {
                role_tags: 'roles',
                intended_actions: 'actions',
                community_location: 'community_scope',
            }[section.renderer];
            if (requiredGroup && section.optionGroupId !== requiredGroup)
                errors.push(`steps.${step.id}.${section.id}: option group must be ${requiredGroup}`);
            for (const id of section.optionGroupIds ?? []) {
                if (!groups.has(id))
                    errors.push(`steps.${step.id}.${section.id}: unknown option group ${id}`);
            }
            if (section.renderer === 'community_location') {
                if (section.required) {
                    for (const id of ['country', 'city']) {
                        if (!section.fields?.some((field) => field.id === id && field.required))
                            errors.push(`steps.${step.id}.${section.id}: ${id} is required`);
                    }
                }
            }
            for (const field of section.fields ?? []) {
                if (field.optionGroupId && !groups.has(field.optionGroupId))
                    errors.push(`steps.${step.id}.${section.id}.${field.id}: unknown option group`);
                if (field.id === 'radius' && field.optionGroupId !== 'community_radius')
                    errors.push(`steps.${step.id}.${section.id}.radius: option group must be community_radius`);
            }
            if (section.renderer === 'privacy' &&
                ['profile_visibility', 'location_visibility', 'group_invitations'].some((id) => !section.optionGroupIds?.includes(id)))
                errors.push(`steps.${step.id}.${section.id}: privacy option groups required`);
        }
    }
    for (const field of identity?.fields ?? []) {
        if (field.optionGroupId && !groups.has(field.optionGroupId))
            errors.push(`identity.${field.id}: unknown option group`);
    }
    if ((groups.get('community_roles')?.options.filter((option) => option.enabled).length ?? 0) < 1)
        errors.push('identity.community_role: at least one enabled option is required');
    unique(flow.detailCards.map((card) => card.id), 'detailCards');
    sorted(flow.detailCards, 'detailCards');
    for (const card of flow.detailCards) {
        unique(card.questions.map((question) => question.id), `detailCards.${card.id}.questions`);
        sorted(card.questions, `detailCards.${card.id}.questions`);
        for (const question of card.questions) {
            const group = groups.get(question.optionGroupId);
            const enabled = group?.options.filter((option) => option.enabled).length ?? 0;
            if (!group)
                errors.push(`detailCards.${card.id}.${question.id}: unknown option group`);
            if (question.maxSelections < question.minSelections || question.minSelections > enabled)
                errors.push(`detailCards.${card.id}.${question.id}: impossible selection bounds`);
            if (question.type === 'single_choice' && question.maxSelections !== 1)
                errors.push(`detailCards.${card.id}.${question.id}: single choice max must be 1`);
        }
    }
    unique(flow.welcomeActions.map((action) => action.id), 'welcomeActions');
    sorted(flow.welcomeActions, 'welcomeActions');
    return errors;
}
//# sourceMappingURL=onboarding.js.map